import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom'; // Import useNavigate
import apiService from '../../services/api';
// TranscriptDisplay is no longer rendered directly here

const SimulationRunner = ({ token }) => {
  const navigate = useNavigate(); // Initialize useNavigate
  const [clientPersonas, setClientPersonas] = useState([]);
  const [therapistPersonas, setTherapistPersonas] = useState([]);
  const [selectedClientPersona, setSelectedClientPersona] = useState('');
  const [selectedTherapistPersona, setSelectedTherapistPersona] = useState('');
  const [maxTurns, setMaxTurns] = useState(100); // Default for extended therapy sessions
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState('');
  const [progress, setProgress] = useState({ current: 0, total: 0 }); // Progress tracking
  const [simulationInProgress, setSimulationInProgress] = useState(false);
  // Removed local transcript and simulationId state as we will navigate

  // Fetch personas on component mount
  useEffect(() => {
    const fetchAllPersonas = async () => {
      if (!token) return;
      setIsLoading(true);
      setError('');
      try {
        const [clients, therapists] = await Promise.all([
          apiService.getClientPersonas(token), // Pass token
          apiService.getTherapistPersonas(token) // Pass token
        ]);
        setClientPersonas(clients || []);
        setTherapistPersonas(therapists || []);
        // Set default selections if available
        if (clients && clients.length > 0) setSelectedClientPersona(clients[0].key);
        if (therapists && therapists.length > 0) setSelectedTherapistPersona(therapists[0].key);
      } catch (err) {
        console.error("Failed to fetch personas for simulator:", err);
        setError("Could not load personas.");
      } finally {
        setIsLoading(false);
      setSimulationInProgress(false);
      setProgress({ current: 0, total: 0 });
      }
    };
    fetchAllPersonas();
  }, [token]);

  // Simulate progress updates
  useEffect(() => {
    if (simulationInProgress && progress.total > 0) {
      const estimatedTimePerTurn = 2000; // 2 seconds per turn estimate
      const totalEstimatedTime = progress.total * estimatedTimePerTurn;
      const incrementInterval = totalEstimatedTime / progress.total;
      
      const interval = setInterval(() => {
        setProgress(prev => {
          if (prev.current >= prev.total - 1) {
            clearInterval(interval);
            return prev;
          }
          return { ...prev, current: prev.current + 1 };
        });
      }, incrementInterval);
      
      return () => clearInterval(interval);
    }
  }, [simulationInProgress, progress.total]);

  const handleRunSimulation = async (e) => {
    e.preventDefault();
    if (!selectedClientPersona || !selectedTherapistPersona || !token) {
      setError("Please select both client and therapist personas.");
      return;
    }
    setIsLoading(true);
    setError('');
    setSimulationInProgress(true);
    setProgress({ current: 0, total: parseInt(maxTurns, 10) || 100 });
    // setTranscript(null); // No longer setting local transcript

    try {
      const params = {
        clientPersonaKey: selectedClientPersona,
        therapistPersonaKey: selectedTherapistPersona,
        maxTurns: parseInt(maxTurns, 10) || 100,
      };
      const result = await apiService.runSimulation(params, token);
      console.log('Simulation run API result:', JSON.stringify(result, null, 2)); // Log the result

      // The backend returns { transcript: [...], simulationId: '...' }
      const actualSimulationId = result?.simulationId;
      const transcript = result?.transcript;
      
      if (transcript && actualSimulationId) {
        console.log(`Navigating to /admin/dashboard/history/${actualSimulationId}`); // Log navigation attempt
        navigate(`/admin/dashboard/history/${actualSimulationId}`, { 
          state: { 
            fromRunner: true,
            // We could pass simulationData.transcript here if SimulationHistoryViewer is set up to use it
            // For now, just navigating with ID and fromRunner flag.
            // SimulationHistoryViewer will fetch details using the ID.
          } 
        });
      } else {
        console.error("Simulation run did not return a valid simulationId or data structure. Received:", result);
        setError("Simulation completed but could not retrieve ID or data to display results.");
      }
    } catch (err) {
      console.error("Simulation run error:", err);
      setError(`Simulation failed: ${err.message}`);
      // Optionally, could still set a local error transcript if needed for some UI element here
      // setTranscript([{ sender: 'system', text: `Simulation failed: ${err.message}` }]);
    } finally {
      setIsLoading(false);
      setSimulationInProgress(false);
      setProgress({ current: 0, total: 0 });
    }
  };

  return (
    <div>
      <h3>Run AI Simulation</h3>
      <form onSubmit={handleRunSimulation}>
        <div>
          <label htmlFor="client-persona">Client Persona: </label>
          <select
            id="client-persona"
            value={selectedClientPersona}
            onChange={(e) => setSelectedClientPersona(e.target.value)}
            disabled={isLoading || clientPersonas.length === 0}
            required
          >
            <option value="" disabled>-- Select Client --</option>
            {clientPersonas.map(p => <option key={p.key} value={p.key}>{p.name}</option>)}
          </select>
        </div>
        <div style={{ marginTop: '10px' }}>
          <label htmlFor="therapist-persona">Therapist Persona: </label>
          <select
            id="therapist-persona"
            value={selectedTherapistPersona}
            onChange={(e) => setSelectedTherapistPersona(e.target.value)}
            disabled={isLoading || therapistPersonas.length === 0}
            required
          >
            <option value="" disabled>-- Select Therapist --</option>
            {therapistPersonas.map(p => <option key={p.key} value={p.key}>{p.name}</option>)}
          </select>
        </div>
        <div style={{ marginTop: '10px' }}>
          <label htmlFor="max-turns">Max Turns: </label>
          <input
            type="number"
            id="max-turns"
            value={maxTurns}
            onChange={(e) => setMaxTurns(e.target.value)}
            min="1"
            max="500" // Extended limit for comprehensive sessions
            disabled={isLoading}
            style={{ width: '60px' }}
          />
          <small style={{ marginLeft: '10px', color: '#666' }}>
            (Goal-based stopping may end earlier)
          </small>
        </div>
        <div style={{ marginTop: '15px' }}>
          <button type="submit" disabled={isLoading}>
            {isLoading ? 'Running Simulation...' : 'Run Simulation'}
          </button>
        </div>
      </form>
      {error && <p style={{ color: 'red', marginTop: '10px' }}>{error}</p>}
      
      {/* Progress Bar */}
      {simulationInProgress && progress.total > 0 && (
        <div style={{ marginTop: '20px' }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '5px' }}>
            <span style={{ fontSize: '14px', color: '#666' }}>
              Simulation Progress
            </span>
            <span style={{ fontSize: '14px', color: '#666' }}>
              Turn {progress.current} of {progress.total}
            </span>
          </div>
          <div style={{ 
            width: '100%', 
            height: '20px', 
            backgroundColor: '#f0f0f0', 
            borderRadius: '10px',
            overflow: 'hidden',
            border: '1px solid #ddd'
          }}>
            <div style={{
              height: '100%',
              width: `${(progress.current / progress.total) * 100}%`,
              backgroundColor: '#4CAF50',
              transition: 'width 0.5s ease-in-out',
              borderRadius: '10px',
              background: 'linear-gradient(90deg, #4CAF50 0%, #45a049 100%)'
            }} />
          </div>
          <p style={{ 
            textAlign: 'center', 
            marginTop: '10px', 
            fontSize: '12px', 
            color: '#666',
            fontStyle: 'italic'
          }}>
            Estimated progress - actual simulation may complete sooner if goals are achieved
          </p>
        </div>
      )}

      {/* TranscriptDisplay is no longer rendered here */}
    </div>
  );
};

export default SimulationRunner;
